import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  DENTAL_STATES_CONFIG,
  STATE_CATEGORIES,
  getStatesForCategory,
  type DentalStateConfig,
  type StateCategory,
  type TargetZone,
  type ToothData,
} from '../../types/records';
import type { SelectionContext } from '../../lib/ngapLogicEngine';
import { getStateHints, type StateHint } from '../../lib/dentalValidation';

interface DentalStatesPanelProps {
  selectionContext: SelectionContext;
  teethData: Record<string, ToothData>;
  onApplyState: (stateCode: string) => void;
  onRemoveLayer?: (toothNum: number, layerIndex: number) => void;
  applyLoading?: boolean;
  /** Validation error message (blocking) */
  validationMessage?: string | null;
  /** Validation guidance (what to do instead) */
  validationGuidance?: string | null;
  /** Non-blocking validation warnings */
  validationWarnings?: string[];
  /** Dismiss the validation message */
  onClearValidation?: () => void;
  /** Custom confirm dialog for destructive actions (replaces window.confirm) */
  onRequestConfirm?: (opts: { title: string; message: string; details?: string; onConfirm: () => void }) => void;
  /** Hover preview: called when mouse enters/leaves a state button (null = clear preview) */
  onHoverState?: (stateCode: string | null) => void;
}

// ── Determine which targetZones are available from the current selection ──
function getAvailableZones(ctx: SelectionContext): Set<TargetZone> {
  const zones = new Set<TargetZone>();

  if (ctx.selectedTeeth.length > 0) {
    zones.add('WHOLE_TOOTH');
    zones.add('CROWN_WHOLE');
    zones.add('ROOT');
    zones.add('NECK');
  }

  if (Object.keys(ctx.selectedSurfaces).length > 0) {
    zones.add('CROWN_SURFACE');
    zones.add('WHOLE_TOOTH');
    zones.add('CROWN_WHOLE');
    zones.add('ROOT');
    zones.add('NECK');
  }

  if (Object.keys(ctx.selectedParts).length > 0) {
    zones.add('WHOLE_TOOTH');
    for (const parts of Object.values(ctx.selectedParts)) {
      for (const part of parts) {
        const p = part.toLowerCase();
        if (p === 'crown') { zones.add('CROWN_WHOLE'); zones.add('CROWN_SURFACE'); }
        if (p === 'root')  { zones.add('ROOT'); }
        if (p === 'neck')  { zones.add('NECK'); }
      }
    }
  }

  return zones;
}

function isStateApplicable(state: DentalStateConfig, availableZones: Set<TargetZone>): boolean {
  return state.targetZones.some(z => availableZones.has(z));
}

// Get the primary tooth number from selection context
function getPrimaryToothNum(ctx: SelectionContext): number | null {
  const t = ctx.selectedTeeth[0];
  if (t) return t;
  const surfKey = Object.keys(ctx.selectedSurfaces)[0];
  if (surfKey) return Number(surfKey);
  const partKey = Object.keys(ctx.selectedParts)[0];
  if (partKey) return Number(partKey);
  return null;
}

// Get all selected tooth numbers
function getAllSelectedTeeth(ctx: SelectionContext): number[] {
  const teeth = new Set<number>();
  for (const t of ctx.selectedTeeth) teeth.add(t);
  for (const fdi of Object.keys(ctx.selectedSurfaces)) teeth.add(Number(fdi));
  for (const fdi of Object.keys(ctx.selectedParts)) teeth.add(Number(fdi));
  return Array.from(teeth).filter(n => n > 0);
}

export default function DentalStatesPanel({
  selectionContext,
  teethData,
  onApplyState,
  onRemoveLayer,
  applyLoading = false,
  validationMessage,
  validationGuidance,
  validationWarnings = [],
  onClearValidation,
  onRequestConfirm,
  onHoverState,
}: DentalStatesPanelProps) {
  const [expandedCategory, setExpandedCategory] = useState<StateCategory | null>('Anatomy & Status');
  const [activeState, setActiveState] = useState<string | null>(null);
  const [lastApplied, setLastApplied] = useState<string | null>(null);

  // Reset the active (radio-selected) state when the tooth selection changes (#1: include surface VALUES)
  const selectionKey = useMemo(
    () => [...selectionContext.selectedTeeth].sort().join(',')
      + '|' + JSON.stringify(selectionContext.selectedSurfaces)
      + '|' + JSON.stringify(selectionContext.selectedParts),
    [selectionContext]
  );
  useEffect(() => {
    setActiveState(null);
  }, [selectionKey]);

  const hasSelection = useMemo(() => {
    return (
      selectionContext.selectedTeeth.length > 0 ||
      Object.keys(selectionContext.selectedSurfaces).length > 0 ||
      Object.keys(selectionContext.selectedParts).length > 0
    );
  }, [selectionContext]);

  const availableZones = useMemo(
    () => getAvailableZones(selectionContext),
    [selectionContext]
  );

  const allSelectedTeeth = useMemo(
    () => getAllSelectedTeeth(selectionContext),
    [selectionContext]
  );

  const primaryToothNum = useMemo(
    () => getPrimaryToothNum(selectionContext),
    [selectionContext]
  );

  const primaryToothData = useMemo(() => {
    if (!primaryToothNum) return null;
    return teethData[String(primaryToothNum)] ?? { base_state: 'PRESENT', layers: [] };
  }, [primaryToothNum, teethData]);

  // ── Auto-expand category matching selected tooth's conditions (#19) ──
  useEffect(() => {
    if (!primaryToothNum) return;
    const tooth = teethData[String(primaryToothNum)];
    if (!tooth) return;

    // Find which category the tooth's layers or base_state belong to
    const layerCodes = (tooth.layers || []).map(l => l.code);
    if (tooth.base_state !== 'PRESENT') layerCodes.push(tooth.base_state);
    if (layerCodes.length === 0) return;

    for (const cat of STATE_CATEGORIES) {
      const states = getStatesForCategory(cat.key);
      if (states.some(s => layerCodes.includes(s.code))) {
        setExpandedCategory(cat.key);
        return;
      }
    }
  }, [primaryToothNum, teethData]);

  // ── Compute per-state hints (blocked/warning/ok) from validation engine ──
  const stateHints = useMemo<Record<string, StateHint>>(() => {
    if (!hasSelection) return {};
    const surfacesPerTooth: Record<string, string[]> = {};
    for (const [fdi, surfs] of Object.entries(selectionContext.selectedSurfaces)) {
      surfacesPerTooth[fdi] = surfs;
    }
    return getStateHints(allSelectedTeeth, teethData, surfacesPerTooth);
  }, [allSelectedTeeth, teethData, selectionContext, hasSelection]);

  // ── Auto-dismiss validation message after 15 seconds (#16: longer for complex messages) ──
  useEffect(() => {
    if (!validationMessage) return;
    const t = setTimeout(() => { onClearValidation?.(); }, 15000);
    return () => clearTimeout(t);
  }, [validationMessage, onClearValidation]);

  // ── Auto-dismiss warnings after 8 seconds ──
  useEffect(() => {
    if (validationWarnings.length === 0) return;
    const t = setTimeout(() => { onClearValidation?.(); }, 8000);
    return () => clearTimeout(t);
  }, [validationWarnings, onClearValidation]);

  const selectionSummary = useMemo(() => {
    const parts: string[] = [];
    const teeth = selectionContext.selectedTeeth;
    const surfs = Object.entries(selectionContext.selectedSurfaces);
    const pparts = Object.entries(selectionContext.selectedParts);

    // Teeth already covered by surfaces or parts — avoid duplication (#18)
    const teethWithDetails = new Set<number>();
    for (const [t] of surfs) teethWithDetails.add(Number(t));
    for (const [t] of pparts) teethWithDetails.add(Number(t));

    const teethOnly = teeth.filter(t => !teethWithDetails.has(t));
    if (teethOnly.length > 0) {
      parts.push(`Dent${teethOnly.length > 1 ? 's' : ''} ${teethOnly.join(', ')}`);
    }
    if (surfs.length > 0) {
      for (const [t, s] of surfs) {
        parts.push(`Dent ${t}: ${s.join(', ')}`);
      }
    }
    if (pparts.length > 0) {
      for (const [t, p] of pparts) {
        parts.push(`Dent ${t}: ${p.join(', ')}`);
      }
    }
    return parts.join(' · ') || 'Aucune sélection';
  }, [selectionContext]);

  // Destructive states that need visual confirmation (#15)
  const DESTRUCTIVE_STATES = new Set(['EXTRACT', 'MISSING']);

  // ── Radio behavior: clicking a state selects it, clicking again deselects ──
  const handleStateClick = useCallback((stateCode: string) => {
    if (applyLoading) return;

    // Clear any previous validation messages
    onClearValidation?.();

    // Auto-apply immediately when teeth are selected
    if (hasSelection) {
      // #15: Confirm destructive states
      if (DESTRUCTIVE_STATES.has(stateCode)) {
        const label = DENTAL_STATES_CONFIG[stateCode]?.label_fr ?? stateCode;
        const teethStr = allSelectedTeeth.join(', ');
        if (onRequestConfirm) {
          onRequestConfirm({
            title: `Appliquer "${label}" ?`,
            message: `Cette action sur les dent(s) ${teethStr} est irréversible.`,
            details: `État : ${label}\nDent(s) : ${teethStr}`,
            onConfirm: () => {
              onApplyState(stateCode);
              setActiveState(stateCode);
              setLastApplied(stateCode);
              setTimeout(() => setLastApplied(null), 1500);
            },
          });
          return;
        }
      }
      onApplyState(stateCode);
      // Set active state AFTER apply attempt (#14: don't show "selected" on blocked states)
      setActiveState(stateCode);
      setLastApplied(stateCode);
      setTimeout(() => setLastApplied(null), 1500);
    } else {
      // No selection — just toggle the state radio
      setActiveState(prev => prev === stateCode ? null : stateCode);
    }
  }, [applyLoading, hasSelection, allSelectedTeeth, onApplyState, onClearValidation]);

  const handleRemoveLayer = useCallback((toothNum: number, layerIndex: number) => {
    if (onRemoveLayer) {
      onRemoveLayer(toothNum, layerIndex);
    }
  }, [onRemoveLayer]);

  const toggleCategory = useCallback((cat: StateCategory) => {
    setExpandedCategory(prev => prev === cat ? null : cat);
  }, []);

  const renderStateButton = (state: DentalStateConfig) => {
    const zoneApplicable = hasSelection && isStateApplicable(state, availableZones);
    const hint = stateHints[state.code];
    const isBlocked = hint?.level === 'blocked';
    const isWarning = hint?.level === 'warning';
    const canApply = zoneApplicable && !isBlocked;
    const isApplied = lastApplied === state.code;
    const isActive = activeState === state.code;
    const isCurrentBase = primaryToothData?.base_state === state.code;
    const isActiveLayer = primaryToothData?.layers?.some(l => l.code === state.code) ?? false;

    const title = isBlocked
      ? `${state.label_fr} — ${hint.reason}`
      : isWarning
        ? `${state.label_fr} — ⚠ ${hint.reason}`
        : !zoneApplicable
          ? `${state.label_fr}${hasSelection ? ' — Cliquez sur une surface ou zone spécifique' : ' — Sélectionnez une dent'}`
          : `${state.label_fr} (${state.label})`;

    return (
      <button
        key={state.code}
        onClick={() => canApply && handleStateClick(state.code)}
        onMouseEnter={() => canApply && onHoverState?.(state.code)}
        onMouseLeave={() => onHoverState?.(null)}
        disabled={!canApply || applyLoading}
        title={title}
        className={`
          group relative flex items-center gap-2 w-full px-2.5 py-[7px] rounded-md text-left
          transition-all duration-150 select-none
          ${isApplied
            ? 'bg-emerald-100 dark:bg-emerald-500/20 ring-1 ring-emerald-400 dark:ring-emerald-400/50 scale-[0.98]'
            : isActive
              ? 'bg-amber-100 dark:bg-amber-500/15 ring-1 ring-amber-400 dark:ring-amber-400/40'
              : isBlocked
                ? 'bg-red-50 dark:bg-red-900/10 opacity-40 cursor-not-allowed'
                : isWarning
                  ? 'bg-yellow-50 dark:bg-yellow-900/10 ring-1 ring-yellow-400 dark:ring-yellow-700/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/20 cursor-pointer'
                  : isCurrentBase || isActiveLayer
                    ? 'bg-muted ring-1 ring-border'
                    : canApply
                      ? 'hover:bg-muted active:bg-muted/80 active:scale-[0.98] cursor-pointer'
                      : 'opacity-35 cursor-not-allowed'
          }
        `}
      >
        <span
          className={`w-3 h-3 rounded-[3px] shrink-0 border border-white/10 flex items-center justify-center text-[10px] font-bold leading-none transition-transform
            ${canApply ? 'group-hover:scale-110' : ''}
          `}
          style={{ backgroundColor: state.color, color: state.color === '#FFFFFF' || state.color === '#E8E8E8' || state.color === '#FFD700' || state.color === '#FFEB3B' || state.color === '#FFC107' ? '#333' : '#fff' }}
        >
          {state.icon || ''}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className={`text-[11px] font-medium truncate ${isBlocked ? 'text-slate-600 line-through' : canApply ? 'text-slate-800 dark:text-slate-200' : 'text-slate-500'}`}>
              {state.label_fr}
            </span>
            {isBlocked && (
              <span className="text-[10px] px-1 py-px rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400/80 font-medium shrink-0" title={hint.reason}>
                bloqué
              </span>
            )}
            {isWarning && !isActive && (
              <span className="text-[10px] px-1 py-px rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400/80 font-medium shrink-0" title={hint.reason}>
                ⚠
              </span>
            )}
            {isActive && (
              <span className="text-[10px] px-1 py-px rounded bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 font-medium shrink-0">
                sélectionné
              </span>
            )}
            {!isActive && !isBlocked && !isWarning && (isCurrentBase || isActiveLayer) && (
              <span className="text-[10px] px-1 py-px rounded bg-slate-200 dark:bg-slate-600/50 text-slate-500 dark:text-slate-400 font-medium shrink-0">
                {isCurrentBase ? 'actif' : 'couche'}
              </span>
            )}
          </div>
          {isBlocked && hint.reason ? (
            <span className="text-[10px] text-red-500 dark:text-red-400/50 truncate block">{hint.reason}</span>
          ) : (
            <span className={`text-[10px] ${canApply ? 'text-slate-500 dark:text-slate-500' : 'text-slate-500 dark:text-slate-600'} truncate block`}>
              {state.label}
            </span>
          )}
        </div>

        <span className={`text-[10px] font-mono uppercase shrink-0 ${canApply ? 'text-slate-500 dark:text-slate-500' : 'text-slate-600 dark:text-slate-600'}`}>
          {state.targetZones[0] === 'WHOLE_TOOTH' ? 'Dent'
            : state.targetZones[0] === 'CROWN_SURFACE' ? 'Surf'
            : state.targetZones[0] === 'CROWN_WHOLE' ? 'Cour'
            : state.targetZones[0] === 'ROOT' ? 'Rac'
            : 'Col'}
        </span>

        {isApplied && (
          <span className="absolute inset-0 rounded-md bg-emerald-400/10 animate-pulse pointer-events-none" />
        )}
      </button>
    );
  };

  return (
    <div className="h-full flex flex-col bg-card text-card-foreground">
      {/* ── Header ── */}
      <div className="shrink-0 px-4 pt-4 pb-2">
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-amber-600 dark:text-amber-400 mb-2">
          États Dentaires
        </h2>

        {hasSelection ? (
          <div className="mb-2 px-2.5 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/20">
            <div className="text-[10px] text-amber-600 dark:text-amber-400/60 uppercase tracking-wide font-semibold mb-0.5">
              Sélection active
              {allSelectedTeeth.length > 1 && (
                <span className="ml-1 text-amber-500 dark:text-amber-300">({allSelectedTeeth.length} dents)</span>
              )}
            </div>
            <div className="text-[10px] text-amber-700 dark:text-amber-300/90 leading-snug">
              {selectionSummary}
            </div>
            {primaryToothData && (
              <div className="mt-1 flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-sm"
                  style={{ backgroundColor: DENTAL_STATES_CONFIG[primaryToothData.base_state]?.color ?? '#4CAF50' }}
                />
                <span className="text-[10px] text-slate-500 dark:text-slate-400">
                  {DENTAL_STATES_CONFIG[primaryToothData.base_state]?.label_fr ?? 'Saine'}
                  {primaryToothData.layers.length > 0 && (
                    <span className="text-slate-500 ml-1">+ {primaryToothData.layers.length} couche{primaryToothData.layers.length > 1 ? 's' : ''}</span>
                  )}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-2 px-2.5 py-2 rounded-lg bg-muted border border-border">
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <svg className="h-3.5 w-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              <span>Sélectionnez une dent dans le schéma pour appliquer un état</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Validation Error Message ── */}
      {validationMessage && (
        <div className="shrink-0 px-3 pb-2">
          <div className="rounded-lg bg-red-100 dark:bg-red-900/25 border border-red-300 dark:border-red-700/30 overflow-hidden animate-in fade-in slide-in-from-top-1">
            <div className="flex items-start gap-2 px-2.5 py-2">
              <svg className="h-3.5 w-3.5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-red-700 dark:text-red-300 font-medium leading-snug">{validationMessage}</p>
                {validationGuidance && (
                  <p className="text-[10px] text-amber-600 dark:text-amber-400/80 mt-1 leading-snug">
                    Conseil : {validationGuidance}
                  </p>
                )}
              </div>
              {onClearValidation && (
                <button
                  onClick={onClearValidation}
                  className="p-0.5 rounded hover:bg-red-200 dark:hover:bg-red-900/40 text-red-400 dark:text-red-500/60 hover:text-red-600 dark:hover:text-red-400 transition-all shrink-0"
                >
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Validation Warnings (non-blocking) ── */}
      {!validationMessage && validationWarnings.length > 0 && (
        <div className="shrink-0 px-3 pb-2">
          <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/15 border border-yellow-300 dark:border-yellow-700/20 overflow-hidden">
            <div className="flex items-start gap-2 px-2.5 py-1.5">
              <svg className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div className="flex-1 min-w-0">
                {validationWarnings.map((w, i) => (
                  <p key={i} className="text-[10px] text-yellow-700 dark:text-yellow-300/80 leading-snug">{w}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Applied Layers (ALL selected teeth #17) ── */}
      {hasSelection && allSelectedTeeth.length > 0 && allSelectedTeeth.some(t => {
        const td = teethData[String(t)];
        return td && td.layers && td.layers.length > 0;
      }) && (
        <div className="shrink-0 px-3 pb-2">
          <div className="rounded-lg bg-muted border border-border overflow-hidden">
            <div className="px-2.5 py-1.5 bg-muted/80 border-b border-border">
              <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">
                Couches appliquées {allSelectedTeeth.length > 1 ? `— ${allSelectedTeeth.length} dents` : `— Dent #${allSelectedTeeth[0]}`}
              </span>
            </div>
            <div className="max-h-[160px] overflow-y-auto custom-scrollbar">
              {allSelectedTeeth.map(toothNum => {
                const td = teethData[String(toothNum)];
                if (!td || !td.layers || td.layers.length === 0) return null;
                return td.layers.map((layer, idx) => {
                  const cfg = DENTAL_STATES_CONFIG[layer.code];
                  return (
                    <div
                      key={`${toothNum}-${layer.code}-${idx}`}
                      className="flex items-center gap-2 px-2.5 py-1.5 border-b border-border last:border-0 hover:bg-muted group"
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-sm shrink-0"
                        style={{ backgroundColor: cfg?.color ?? '#666' }}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] text-slate-700 dark:text-slate-300 truncate block">
                          {allSelectedTeeth.length > 1 ? `#${toothNum} ` : ''}{cfg?.label_fr ?? layer.code}
                        </span>
                        {layer.surfaces.length > 0 && (
                          <span className="text-[10px] text-slate-500 truncate block">
                            {layer.surfaces.join(', ')}
                          </span>
                        )}
                      </div>
                      {onRemoveLayer && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveLayer(toothNum, idx);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-red-900/40 text-slate-500 hover:text-red-400 transition-all shrink-0"
                          title="Supprimer cette couche"
                        >
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                });
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Categories & States ── */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 custom-scrollbar">
        {STATE_CATEGORIES.map(cat => {
          const states = getStatesForCategory(cat.key);
          const isExpanded = expandedCategory === cat.key;
          const applicableCount = hasSelection
            ? states.filter(s => isStateApplicable(s, availableZones)).length
            : 0;

          return (
            <div key={cat.key} className="mb-1">
              <button
                onClick={() => toggleCategory(cat.key)}
                aria-expanded={isExpanded}
                className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-muted transition-colors rounded-lg group"
              >
                <span
                  className="w-1.5 h-4 rounded-full shrink-0"
                  style={{ backgroundColor: cat.color }}
                />
                <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 flex-1 truncate">
                  {cat.label_fr}
                </span>
                {hasSelection && (
                  <span className={`text-[10px] px-1.5 py-px rounded-full font-medium ${
                    applicableCount > 0 ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400' : 'bg-slate-200 dark:bg-slate-700/50 text-slate-400 dark:text-slate-600'
                  }`}>
                    {applicableCount}/{states.length}
                  </span>
                )}
                <svg
                  className={`h-3 w-3 text-slate-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {isExpanded && (
                <div className="pl-1.5 space-y-[2px] pb-1.5 pt-0.5">
                  {states.map(state => renderStateButton(state))}
                </div>
              )}
            </div>
          );
        })}

        {hasSelection && primaryToothData && primaryToothData.base_state !== 'PRESENT' && (
          <div className="mt-3 pt-3 border-t border-border">
            <button
              onClick={() => handleStateClick('PRESENT')}
              disabled={applyLoading}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg 
                bg-muted hover:bg-muted/80 border border-border
                text-[11px] text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-all"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Réinitialiser à Saine (PRESENT)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
